import type { StreamEvent } from "./stream.ts";

export interface ToolActivity {
  id: string;
  server: string;
  name: string;
  status: "running" | "done" | "failed";
}

export interface ApprovalRequest {
  id: string;
  server: string;
  name: string;
  args: Record<string, unknown>;
  decision?: "approved" | "rejected";
}

export interface Turn {
  id: string;
  role: "user" | "assistant";
  text: string;
  tools: ToolActivity[];
  approvals: ApprovalRequest[];
  consent?: { link: string; server: string };
  error?: string;
  done: boolean;
}

export interface ChatState {
  turns: Turn[];
  previousResponseId?: string;
  sessionId?: string;
  busy: boolean;
  /** Last user message, resent after the user completes a sign-in/consent. */
  lastMessage?: string;
}

export type Action =
  | { type: "user"; id: string; text: string }
  | { type: "decision"; approvalId: string; decision: "approved" | "rejected" }
  | { type: "start"; id: string }
  | { type: "event"; event: StreamEvent }
  | { type: "fail"; message: string }
  | { type: "reset" };

export const initialState: ChatState = { turns: [], busy: false };

const str = (v: unknown) => (typeof v === "string" ? v : undefined);

function parseArgs(raw: unknown): Record<string, unknown> {
  if (raw && typeof raw === "object") return raw as Record<string, unknown>;
  if (typeof raw !== "string") return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
  } catch {
    return { arguments: raw };
  }
}

function updateLast(state: ChatState, update: (t: Turn) => Turn): ChatState {
  const last = state.turns.at(-1);
  if (!last || last.role !== "assistant") return state;
  return { ...state, turns: [...state.turns.slice(0, -1), update(last)] };
}

const TOOL_ITEMS = new Set(["mcp_call", "function_call"]);

function applyItem(turn: Turn, item: Record<string, unknown>, done: boolean): Turn {
  const type = str(item.type);
  const id = str(item.id) ?? str(item.call_id) ?? "";
  if (type && TOOL_ITEMS.has(type)) {
    const status: ToolActivity["status"] = !done ? "running" : item.error || item.status === "failed" ? "failed" : "done";
    const activity: ToolActivity = { id, server: str(item.server_label) ?? "", name: str(item.name) ?? "verktyg", status };
    const exists = turn.tools.some((t) => t.id === id);
    return { ...turn, tools: exists ? turn.tools.map((t) => (t.id === id ? activity : t)) : [...turn.tools, activity] };
  }
  if (type === "mcp_approval_request" && done && !turn.approvals.some((a) => a.id === id)) {
    const request: ApprovalRequest = {
      id,
      server: str(item.server_label) ?? "",
      name: str(item.name) ?? "",
      args: parseArgs(item.arguments),
    };
    return { ...turn, approvals: [...turn.approvals, request] };
  }
  if (type === "oauth_consent_request" && done) {
    const link = str(item.consent_link);
    if (link?.startsWith("https://")) return { ...turn, consent: { link, server: str(item.server_label) ?? "" } };
  }
  return turn;
}

function applyEvent(state: ChatState, { event, data }: StreamEvent): ChatState {
  switch (event) {
    case "response.output_text.delta":
      return updateLast(state, (t) => ({ ...t, text: t.text + (str(data.delta) ?? "") }));
    case "response.output_item.added":
    case "response.output_item.done": {
      const item = data.item as Record<string, unknown> | undefined;
      return item ? updateLast(state, (t) => applyItem(t, item, event.endsWith("done"))) : state;
    }
    case "response.created":
    case "response.completed":
    case "response.incomplete":
    case "response.failed": {
      const response = (data.response ?? {}) as Record<string, unknown>;
      const next: ChatState = {
        ...state,
        previousResponseId: str(response.id) ?? state.previousResponseId,
        sessionId: str(response.agent_session_id) ?? state.sessionId,
      };
      if (event === "response.created") return next;
      const failed = event === "response.failed";
      const reason = str((response.error as Record<string, unknown> | undefined)?.message);
      return updateLast({ ...next, busy: false }, (t) => ({
        ...t,
        done: true,
        error: failed ? reason ?? "Agenten kunde inte slutföra svaret." : t.error,
      }));
    }
    case "error":
      return updateLast({ ...state, busy: false }, (t) => ({
        ...t,
        done: true,
        error: str(data.message) ?? "Något gick fel.",
      }));
    default:
      return state;
  }
}

const emptyTurn = (id: string, role: Turn["role"], text = ""): Turn => ({
  id,
  role,
  text,
  tools: [],
  approvals: [],
  done: role === "user",
});

export function reducer(state: ChatState, action: Action): ChatState {
  switch (action.type) {
    case "user":
      return { ...state, lastMessage: action.text, turns: [...state.turns, emptyTurn(action.id, "user", action.text)] };
    case "decision":
      return {
        ...state,
        turns: state.turns.map((t) => ({
          ...t,
          approvals: t.approvals.map((a) => (a.id === action.approvalId ? { ...a, decision: action.decision } : a)),
        })),
      };
    case "start":
      return { ...state, busy: true, turns: [...state.turns, emptyTurn(action.id, "assistant")] };
    case "event":
      return applyEvent(state, action.event);
    case "fail":
      return updateLast({ ...state, busy: false }, (t) => ({ ...t, done: true, error: action.message }));
    case "reset":
      return initialState;
  }
}
